<template>
  <div class="spinner-wrapper" role="status" aria-live="polite">
    <div
      class="orbit-spinner"
      :style="spinnerStyle"
      aria-hidden="true"
    >
      <!-- Outer subtle ring -->
      <span class="orbit-track"></span>

      <!-- Animated orbital rings -->
      <span class="orbit-ring orbit-ring-one"></span>
      <span class="orbit-ring orbit-ring-two"></span>
      <span class="orbit-ring orbit-ring-three"></span>

      <!-- Center glow -->
      <!-- <span class="orbit-center">
        <span class="center-glow"></span>
        <span class="center-dot"></span>
      </span> -->
    </div>

    <div v-if="text" class="spinner-caption">
      <span class="spinner-text">{{ text }}</span>
      <span class="loading-dots" aria-hidden="true">
        <i></i>
        <i></i>
        <i></i>
      </span>
    </div>
  </div>
</template>

<script>
export default {
  name: "LoadingSpinner",

  props: {
    size: {
      type: Number,
      default: 44
    },
    text: {
      type: String,
      default: "Loading"
    }
  },

  computed: {
    spinnerStyle() {
      return {
        width: `${this.size}px`,
        height: `${this.size}px`
      };
    }
  }
};
</script>

<style scoped>
.spinner-wrapper {
  width: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 15px;
  padding: 24px;
  box-sizing: border-box;
}

/* Main spinner */
.orbit-spinner {
  position: relative;
  flex-shrink: 0;
  display: grid;
  place-items: center;
  border-radius: 50%;
  isolation: isolate;
}

/* Soft outer boundary */
.orbit-track {
  position: absolute;
  inset: 0;
  border: 1px solid rgba(100, 130, 190, 0.12);
  border-radius: 50%;
  box-sizing: border-box;
}

/* Shared orbital ring */
.orbit-ring {
  position: absolute;
  border-radius: 50%;
  border: 2px solid transparent;
  box-sizing: border-box;
  will-change: transform;
}

.orbit-ring-one {
  inset: 0;
  border-top-color: #4389ff;
  border-right-color: #4389ff;
  filter: drop-shadow(0 0 3px rgba(67, 137, 255, 0.2));
  animation: orbit-clockwise 1.8s cubic-bezier(0.5, 0, 0.5, 1) infinite;
}

.orbit-ring-two {
  inset: 5px;
  border-bottom-color: #9b70f5;
  border-left-color: #9b70f5;
  animation: orbit-counterclockwise 2.5s linear infinite;
}

.orbit-ring-three {
  inset: 10px;
  border-top-color: #65c9f5;
  border-right-color: #65c9f5;
  border-width: 2px;
  animation: orbit-clockwise 1.25s linear infinite;
}

/* Refined center */
.orbit-center {
  position: absolute;
  inset: 36%;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: linear-gradient(
    145deg,
    #ffffff 5%,
    #f0f4ff 100%
  );
  border: 1px solid rgba(116, 145, 220, 0.18);
  box-shadow:
    0 2px 8px rgba(53, 89, 165, 0.08),
    inset 0 1px 2px rgba(255, 255, 255, 0.9);
}

.center-glow {
  position: absolute;
  inset: -3px;
  border-radius: 50%;
  background: linear-gradient(
    135deg,
    rgba(67, 137, 255, 0.12),
    rgba(155, 112, 245, 0.12)
  );
  filter: blur(4px);
  animation: glow-breathe 2s ease-in-out infinite;
}

.center-dot {
  position: relative;
  width: 38%;
  height: 38%;
  border-radius: 50%;
  background: linear-gradient(135deg, #51b4ff, #7965f5);
  box-shadow: 0 0 7px rgba(106, 119, 245, 0.24);
  animation: dot-breathe 1.8s ease-in-out infinite;
}

/* Elegant caption */
.spinner-caption {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 18px;
}

.spinner-text {
  color: #64748b;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.35px;
  line-height: 1.5;
}

/* Three understated loading dots */
.loading-dots {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding-top: 2px;
}

.loading-dots i {
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #6485e8;
  opacity: 0.3;
  animation: dot-wave 1.2s ease-in-out infinite;
}

.loading-dots i:nth-child(2) {
  animation-delay: 0.15s;
}

.loading-dots i:nth-child(3) {
  animation-delay: 0.3s;
}

/* Animations */
@keyframes orbit-clockwise {
  to {
    transform: rotate(360deg);
  }
}

@keyframes orbit-counterclockwise {
  to {
    transform: rotate(-360deg);
  }
}

@keyframes glow-breathe {
  0%, 100% {
    opacity: 0.5;
    transform: scale(0.92);
  }
  50% {
    opacity: 1;
    transform: scale(1.08);
  }
}

@keyframes dot-breathe {
  0%, 100% {
    transform: scale(0.85);
    opacity: 0.85;
  }
  50% {
    transform: scale(1.05);
    opacity: 1;
  }
}

@keyframes dot-wave {
  0%, 60%, 100% {
    opacity: 0.3;
    transform: translateY(0);
  }
  30% {
    opacity: 1;
    transform: translateY(-2px);
  }
}

/* Accessibility */
@media (prefers-reduced-motion: reduce) {
  .orbit-ring,
  .center-glow,
  .center-dot,
  .loading-dots i {
    animation: none;
  }

  .loading-dots i {
    opacity: 0.7;
  }
}
</style>
